import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function BroadcastBanner() {
  const [broadcasts, setBroadcasts] = useState([]);

  useEffect(() => {
    fetchBroadcasts();
  }, []);

  const fetchBroadcasts = async () => {
    try {
      const res = await axios.get('/api/notifications/broadcasts');
      setBroadcasts(res.data.broadcasts || []);
    } catch (error) {
      // Silently fail — banner is non-critical
    }
  };

  const handleDismiss = async (id) => {
    try {
      await axios.post(`/api/notifications/broadcasts/${id}/dismiss`);
      setBroadcasts(prev => prev.filter(b => b.id !== id));
    } catch (error) {
      // Dismiss locally even if API fails
      setBroadcasts(prev => prev.filter(b => b.id !== id));
    }
  };

  if (broadcasts.length === 0) return null;

  return (
    <div className="space-y-2 mb-6" data-testid="broadcast-banner-container">
      {broadcasts.map(broadcast => (
        <div
          key={broadcast.id}
          className="relative flex items-center gap-4 p-4 rounded-xl border-l-4 border-l-primary bg-primary/5 border border-border/50 animate-in slide-in-from-top-2 duration-300"
          data-testid={`broadcast-banner-${broadcast.id}`}
        >
          {/* Accent dot */}
          <div className="shrink-0 w-2 h-2 rounded-full bg-primary" />

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-foreground">{broadcast.title}</p>
            <p className="text-sm text-muted-foreground truncate">{broadcast.message}</p>
          </div>

          {/* CTA */}
          {broadcast.link && (
            <a
              href={broadcast.link}
              className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              {broadcast.ctaLabel || 'View'} <ArrowRight className="w-3 h-3" />
            </a>
          )}

          {/* Dismiss */}
          {broadcast.dismissible !== false && (
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => handleDismiss(broadcast.id)}
              data-testid={`dismiss-broadcast-${broadcast.id}`}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
